import { createSlice, PayloadAction } from '@reduxjs/toolkit'

// Types
interface UIState {
  // Layout states
  showHeader: boolean
  showFooter: boolean

  // Loading states
  globalLoading: boolean

  // Error states
  globalError: string | null

  // Search states
  isSearchOpen: boolean
  searchQuery: string

  // Cart states
  isCartOpen: boolean

  // Login popup
  isLoginPopupOpen: boolean

  // Logout popup
  isLogoutPopupOpen: boolean

  // Mobile menu
  isMobileMenuOpen: boolean

  // Filters
  isFiltersOpen: boolean

  // Notifications
  notifications: Array<{
    id: string
    type: 'success' | 'error' | 'warning' | 'info'
    message: string
    timestamp: number
  }>
}

const initialState: UIState = {
  showHeader: true,
  showFooter: true,
  globalLoading: false,
  globalError: null,
  isSearchOpen: false,
  searchQuery: '',
  isCartOpen: false,
  isLoginPopupOpen: false,
  isLogoutPopupOpen: false,
  isMobileMenuOpen: false,
  isFiltersOpen: false,
  notifications: [],
}

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    // Layout actions
    setShowHeader: (state, action: PayloadAction<boolean>) => {
      state.showHeader = action.payload
    },
    setShowFooter: (state, action: PayloadAction<boolean>) => {
      state.showFooter = action.payload
    },
    setLayout: (state, action: PayloadAction<{ showHeader: boolean; showFooter: boolean }>) => {
      state.showHeader = action.payload.showHeader
      state.showFooter = action.payload.showFooter
    },

    // Loading actions
    setGlobalLoading: (state, action: PayloadAction<boolean>) => {
      state.globalLoading = action.payload
    },

    // Error actions
    setGlobalError: (state, action: PayloadAction<string | null>) => {
      state.globalError = action.payload
    },
    clearGlobalError: state => {
      state.globalError = null
    },

    // Search actions
    setSearchOpen: (state, action: PayloadAction<boolean>) => {
      state.isSearchOpen = action.payload
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload
    },
    toggleSearch: state => {
      state.isSearchOpen = !state.isSearchOpen
    },

    // Cart actions
    setCartOpen: (state, action: PayloadAction<boolean>) => {
      state.isCartOpen = action.payload
    },
    toggleCart: state => {
      state.isCartOpen = !state.isCartOpen
    },

    // Login popup actions
    setLoginPopupOpen: (state, action: PayloadAction<boolean>) => {
      state.isLoginPopupOpen = action.payload
    },
    toggleLoginPopup: state => {
      state.isLoginPopupOpen = !state.isLoginPopupOpen
    },

    // Logout popup actions
    setLogoutPopupOpen: (state, action: PayloadAction<boolean>) => {
      state.isLogoutPopupOpen = action.payload
    },
    closeLogoutPopup: state => {
      state.isLogoutPopupOpen = false
    },

    // Mobile menu actions
    setMobileMenuOpen: (state, action: PayloadAction<boolean>) => {
      state.isMobileMenuOpen = action.payload
    },
    toggleMobileMenu: state => {
      state.isMobileMenuOpen = !state.isMobileMenuOpen
    },

    // Filters actions
    setFiltersOpen: (state, action: PayloadAction<boolean>) => {
      state.isFiltersOpen = action.payload
    },
    toggleFilters: state => {
      state.isFiltersOpen = !state.isFiltersOpen
    },

    // Notification actions
    addNotification: (
      state,
      action: PayloadAction<{
        type: 'success' | 'error' | 'warning' | 'info'
        message: string
      }>,
    ) => {
      const notification = {
        id: Date.now().toString(),
        ...action.payload,
        timestamp: Date.now(),
      }
      state.notifications.push(notification)
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(n => n.id !== action.payload)
    },
    clearNotifications: state => {
      state.notifications = []
    },

    // Reset UI state
    resetUI: state => {
      return { ...initialState, notifications: state.notifications }
    },
  },
})

export const {
  setShowHeader,
  setShowFooter,
  setLayout,
  setGlobalLoading,
  setGlobalError,
  clearGlobalError,
  setSearchOpen,
  setSearchQuery,
  toggleSearch,
  setCartOpen,
  toggleCart,
  setLoginPopupOpen,
  toggleLoginPopup,
  setLogoutPopupOpen,
  closeLogoutPopup,
  setMobileMenuOpen,
  toggleMobileMenu,
  setFiltersOpen,
  toggleFilters,
  addNotification,
  removeNotification,
  clearNotifications,
  resetUI,
} = uiSlice.actions

// Selectors
export const selectIsLogoutPopupOpen = (state: { ui: UIState }) => state.ui.isLogoutPopupOpen
export const selectIsLoginPopupOpen = (state: { ui: UIState }) => state.ui.isLoginPopupOpen
export const selectShowHeader = (state: { ui: UIState }) => state.ui.showHeader
export const selectShowFooter = (state: { ui: UIState }) => state.ui.showFooter

export default uiSlice.reducer
